"use client";

import { useState, useEffect, useCallback, useMemo, Suspense, lazy, type ReactNode } from "react";
import { toolHref } from "@/lib/tool-href";
import { ToolInterface } from "@/components/tools/dynamic-tool-loader";
import { TableOfContents, type TocItem } from "@/components/layout/table-of-contents";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FeatureBadgesGroup } from "@/components/ui/feature-badge";
import { NextStepCTA } from "@/components/ui/next-step-cta";
import { TryExamples } from "@/components/ui/try-examples";
import { ExampleUrlListener } from "@/components/tools/example-url-listener";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { TrustPills } from "@/components/ui/trust-pills";
import Link from "next/link";
import {
  CircleCheck, CircleAlert,
  Lightbulb, BookOpen, ArrowRight, ChevronRight,
  Copy, FileText, ExternalLink, FolderOpen, Keyboard,
  Activity, Star, Bookmark, type LucideIcon,
} from "lucide-react";
import { COPY_SHORTCUT_KEY, dispatchToolShortcut, isToolShortcutEvent } from "@/lib/tool-shortcuts";
import { copyText } from "@/lib/clipboard";
import { parseFaqItem } from "@/lib/faq";
import { useSearchParams } from "next/navigation";
import { workflows } from "@/lib/data/workflows";
import { dispatchLoadExample, TOOL_READY_EVENT, type ToolReadyDetail } from "@/lib/load-example";
import { useNetworkRequestCount } from "@/lib/network-monitor";
import { siteConfig } from "@/lib/data/site-config";
import { recordToolView, usePinnedTools } from "@/lib/personalize";
import { InContentAd, SidebarAd } from "@/components/ads";
import { adSlots } from "@/lib/data/ads";

const EmbedWidget = lazy(() => import("@/components/embed-widget").then((m) => ({ default: m.EmbedWidget })));
const FinanceDisclaimer = lazy(() => import("@/components/tools/finance/finance-disclaimer").then((m) => ({ default: m.FinanceDisclaimer })));
const ShareButtons = lazy(() => import("@/components/tools/utilities/share-buttons").then((m) => ({ default: m.ShareButtons })));
const ToolCard = lazy(() => import("@/components/ui/tool-card").then((m) => ({ default: m.ToolCard })));
const GlossaryRelated = lazy(() => import("@/components/tools/glossary-related").then((m) => ({ default: m.GlossaryRelated })));

interface ToolData {
  id: string;
  name: string;
  description: string;
  category: string;
  slug: string;
  popularity: number;
  featured?: boolean;
  trending?: boolean;
  new?: boolean;
  icon?: string;
  examples?: string[];
}

interface ToolContent {
  whatItDoes: string;
  whyItExists: string;
  whoShouldUse: string;
  useCases: string[];
  instructions: string[];
  examples: string[];
  bestPractices: string[];
  commonMistakes: string[];
  faq: string[];
  features?: string[];
  references?: { label: string; url: string }[];
}

interface ToolClientProps {
  tool: ToolData;
  content: ToolContent;
  sameCategory: ToolData[];
  related: ToolData[];
  popularTools: ToolData[];
  specificGuide: { slug: string; title: string; description: string; readTime: string } | null;
  tocItems: TocItem[];
  mainSiteUrl: string;
  categorySlug?: string;
  nextSteps?: { tool: string; label: string; href?: string }[];
}

function ToolActions({ copied, onCopy }: { copied: boolean; onCopy: () => void }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Tool actions">
      <Button variant="ghost" size="sm" onClick={onCopy} aria-label="Copy output (Ctrl/Cmd+Shift+.)">
        <Copy className="h-3.5 w-3.5" aria-hidden="true" />
        <span className="hidden sm:inline">{copied ? "Copied!" : "Copy"}</span>
        <kbd
          className="ml-1 inline-flex h-5 items-center gap-0.5 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1.5 font-mono text-[10px] font-medium text-[var(--color-text-muted)]"
          aria-hidden="true"
        >
          <span>Ctrl/Cmd</span>
          <span className="text-[var(--color-text-muted)]">+</span>
          <span>Shift</span>
          <span className="text-[var(--color-text-muted)]">+</span>
          <span>.</span>
        </kbd>
      </Button>
    </div>
  );
}

async function copyToolOutput(slug: string): Promise<boolean> {
  const container = document.getElementById(`tool-interface-${slug}`);
  if (!container) return false;

  const output =
    container.querySelector<HTMLElement>('[data-testid="tool-output"]') ??
    container.querySelector<HTMLTextAreaElement>("textarea[readonly]") ??
    container.querySelector<HTMLElement>("pre");

  let text = "";
  if (output instanceof HTMLTextAreaElement) {
    text = output.value;
  } else if (output) {
    text = output.innerText;
  }

  if (text.trim()) {
    return copyText(text);
  }

  const selection = window.getSelection()?.toString() ?? "";
  return selection ? copyText(selection) : false;
}

interface QuickLink {
  href: string;
  label: string;
  icon: ReactNode;
  external?: boolean;
}

function QuickLinks({ tool, specificGuide, categorySlug, mainSiteUrl, toolsRepo }: {
  tool: ToolData;
  specificGuide: ToolClientProps["specificGuide"];
  categorySlug?: string;
  mainSiteUrl: string;
  toolsRepo?: string;
}) {
  const links: QuickLink[] = [
    {
      href: `${toolHref(tool)}#examples`,
      label: "Examples",
      icon: <FileText className="h-3.5 w-3.5" aria-hidden="true" />,
    },
    {
      href: specificGuide ? `/guides/${specificGuide.slug}` : `/guides`,
      label: specificGuide ? "Guide" : "Guides",
      icon: <BookOpen className="h-3.5 w-3.5" aria-hidden="true" />,
    },
  ];

  if (categorySlug) {
    links.push({
      href: `/categories/${categorySlug}`,
      label: `More ${tool.category}`,
      icon: <FolderOpen className="h-3.5 w-3.5" aria-hidden="true" />,
    });
  }

  links.push({
    href: mainSiteUrl,
    label: "DevStackIO",
    icon: <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />,
    external: true,
  });

  // Open-source transparency: link to the tool's source file in the public
  // repo via GitHub's code search, so the privacy/auditability promise is
  // verifiable in one click. The search URL is stable even when files
  // move between subdirectories (formatters/, crypto/, utilities/, ...).
  // We only render the link if a toolsRepo is configured in siteConfig.
  if (toolsRepo) {
    links.push({
      href: `${toolsRepo}/search?q=${encodeURIComponent(`"${tool.slug}" path:src/components/tools`)}&type=code`,
      label: "View source",
      icon: <Copy className="h-3.5 w-3.5" aria-hidden="true" />,
      external: true,
    });
  }

  return (
    <nav className="mt-3 flex flex-wrap items-center gap-2 text-sm" aria-label="Quick links">
      {links.map((link) => (
        <a
          key={link.href}
          href={link.href}
          target={link.external ? "_blank" : undefined}
          rel={link.external ? "noopener noreferrer" : undefined}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs font-medium text-[var(--color-text-muted)] transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)] hover:text-[var(--color-accent-hover)]"
        >
          {link.icon}
          <span>{link.label}</span>
        </a>
      ))}
    </nav>
  );
}

function FavoriteToggle({ slug, name }: { slug: string; name: string }) {
  const { isPinned, toggle } = usePinnedTools();
  const pinned = isPinned(slug);
  return (
    <button
      type="button"
      onClick={() => toggle(slug)}
      aria-label={pinned ? `Remove ${name} from favorites` : `Add ${name} to favorites`}
      aria-pressed={pinned}
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors ${pinned ? "border-[var(--color-accent)] bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)]" : "border-[var(--color-border)] bg-[var(--color-surface)] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent-hover)]"}`}
    >
      {pinned ? <Star className="h-3.5 w-3.5 fill-current" aria-hidden="true" /> : <Bookmark className="h-3.5 w-3.5" aria-hidden="true" />}
      {pinned ? "Favorited" : "Add to favorites"}
    </button>
  );
}

function SectionHeading({ children, icon: Icon }: { children: ReactNode; icon?: LucideIcon }) {
  return (
    <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text)]">
      {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />}
      {children}
    </h2>
  );
}

function CollapsibleSection({
  id,
  title,
  icon: Icon,
  children,
  defaultOpen = false,
}: {
  id: string;
  title: string;
  icon?: LucideIcon;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details
      id={id}
      open={defaultOpen}
      className="group rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]"
    >
      <summary className="flex cursor-pointer list-none items-center justify-between gap-2 px-4 py-3 [&::-webkit-details-marker]:hidden">
        <h2 className="flex items-center gap-2 text-lg font-semibold text-[var(--color-text)]">
          {Icon && <Icon className="h-5 w-5 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />}
          {title}
        </h2>
        <ChevronRight className="h-4 w-4 flex-shrink-0 text-[var(--color-text-muted)] transition-transform group-open:rotate-90" aria-hidden="true" />
      </summary>
      <div className="px-4 pb-4">{children}</div>
    </details>
  );
}

function WorkflowTracker({
  onUpdate,
}: {
  onUpdate: (s: {
    workflow: (typeof workflows)[number] | null;
    currentStepIdx: number;
    nextWorkflowStep: (typeof workflows)[number]["steps"][number] | null;
  }) => void;
}) {
  const searchParams = useSearchParams();
  const workflowSlug = searchParams.get("workflow");
  const stepParam = searchParams.get("step");
  const workflow = workflowSlug ? workflows.find((w) => w.slug === workflowSlug) ?? null : null;
  const currentStepIdx = workflow && stepParam ? Math.max(0, parseInt(stepParam, 10) - 1) : -1;
  const nextWorkflowStep =
    workflow && currentStepIdx >= 0 && currentStepIdx + 1 < workflow.steps.length
      ? workflow.steps[currentStepIdx + 1]
      : null;

  useEffect(() => {
    onUpdate({ workflow, currentStepIdx, nextWorkflowStep });
  }, [workflow, currentStepIdx, nextWorkflowStep, onUpdate]);

  return null;
}

export function ToolClient({
  tool,
  content,
  sameCategory,
  related,
  popularTools,
  specificGuide,
  tocItems,
  mainSiteUrl,
  categorySlug,
  nextSteps = [],
}: ToolClientProps) {
  const [activeTocId, setActiveTocId] = useState("");
  const [copied, setCopied] = useState(false);
  // Example buttons stay disabled until the lazy tool component mounts and
  // announces readiness — otherwise a fast click lands before any subscriber
  // exists and silently does nothing. Readiness is tracked per slug (a set,
  // never reset) so no effect needs a synchronous setState.
  const [readySlugs, setReadySlugs] = useState<ReadonlySet<string>>(new Set());
  const toolReady = readySlugs.has(tool.slug);

  useEffect(() => {
    const onReady = (event: Event) => {
      const detail = (event as CustomEvent<ToolReadyDetail>).detail;
      if (detail && detail.slug) {
        setReadySlugs((prev) => (prev.has(detail.slug) ? prev : new Set(prev).add(detail.slug)));
      }
    };
    window.addEventListener(TOOL_READY_EVENT, onReady);
    return () => window.removeEventListener(TOOL_READY_EVENT, onReady);
  }, []);

  const handleCopy = useCallback(async () => {
    const ok = await copyToolOutput(tool.slug);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  }, [tool.slug]);

  const handleLoadExample = useCallback(
    (text: string) => {
      dispatchLoadExample(tool.slug, text);
    },
    [tool.slug],
  );

  const { count: requestCount, recent: recentRequests } = useNetworkRequestCount();

  // Record this tool as "recently used" once on mount. SSR-safe: hooks only
  // fire on the client, so the localStorage write never runs on the server.
  useEffect(() => {
    recordToolView(tool.slug);
  }, [tool.slug]);

  const networkPillTitle = useMemo(() => {
    if (requestCount === 0) {
      return "This page has made 0 outbound network requests since it loaded. Your tool input never leaves the browser.";
    }
    const paths = recentRequests.map((r) => {
      try {
        return new URL(r.url, window.location.href).pathname;
      } catch {
        return r.url;
      }
    });
    const list = paths.length ? `\nRecent: ${paths.join(", ")}` : "";
    return `This page has made ${requestCount} outbound network request(s) since it loaded — none of them carried your tool input.${list}`;
  }, [requestCount, recentRequests]);

  useEffect(() => {
    const onShortcut = (e: KeyboardEvent) => {
      if (isToolShortcutEvent(e, "Enter", false)) {
        e.preventDefault();
        dispatchToolShortcut("run");
      } else if (isToolShortcutEvent(e, COPY_SHORTCUT_KEY, true)) {
        e.preventDefault();
        handleCopy();
      } else if (isToolShortcutEvent(e, "M", true)) {
        e.preventDefault();
        dispatchToolShortcut("minify");
      } else if (isToolShortcutEvent(e, "V", true)) {
        e.preventDefault();
        dispatchToolShortcut("validate");
      }
    };
    document.addEventListener("keydown", onShortcut);
    return () => document.removeEventListener("keydown", onShortcut);
  }, [handleCopy]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveTocId(entry.target.id);
          }
        });
      },
      { rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );

    tocItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [tocItems]);

  const relatedList = [...sameCategory, ...related, ...popularTools].slice(0, 8);

  // Workflow banner state lifted from inner component via callback
  const [workflowState, setWorkflowState] = useState<{
    workflow: (typeof workflows)[number] | null;
    currentStepIdx: number;
    nextWorkflowStep: (typeof workflows)[number]["steps"][number] | null;
  }>({ workflow: null, currentStepIdx: -1, nextWorkflowStep: null });
  const workflow = workflowState.workflow;
  const currentStepIdx = workflowState.currentStepIdx;
  const nextWorkflowStep = workflowState.nextWorkflowStep;

  const handleWorkflowNext = useCallback(async () => {
    if (!workflow || !nextWorkflowStep) return;
    let payload = "";
    try {
      const container = document.getElementById(`tool-interface-${tool.slug}`);
      const output =
        container?.querySelector<HTMLElement>('[data-testid="tool-output"]') ??
        container?.querySelector<HTMLTextAreaElement>("textarea[readonly]");
      if (output instanceof HTMLTextAreaElement) payload = output.value;
      else if (output) payload = (output as HTMLElement).innerText ?? "";
    } catch {
      payload = "";
    }
    const nextUrl = new URL(`/tools/${nextWorkflowStep.toolSlug}`, window.location.origin);
    nextUrl.searchParams.set("workflow", workflow.slug);
    nextUrl.searchParams.set("step", String(currentStepIdx + 2));
    if (payload && payload.length < 1800) {
      nextUrl.searchParams.set("input", payload.slice(0, 1800));
    } else if (payload) {
      try {
        localStorage.setItem(`workflow:${workflow.slug}:step:${currentStepIdx + 2}`, payload.slice(0, 10000));
      } catch {
        // ignore quota
      }
    }
    try {
      const gtag = (window as unknown as { gtag?: (...a: unknown[]) => void }).gtag;
      gtag?.("event", "tool_next_step_click", {
        current_tool: tool.slug,
        next_tool: nextWorkflowStep.toolSlug,
        workflow: workflow.slug,
      });
    } catch {
      // noop
    }
    window.location.href = nextUrl.toString();
  }, [workflow, nextWorkflowStep, currentStepIdx, tool.slug]);

  return (
    <>
      <TableOfContents items={tocItems} activeId={activeTocId} />

      <div className="container py-4 md:py-5">
        <Breadcrumb
          items={[
            { name: "Home", href: "/" },
            { name: "Tools", href: "/tools" },
            ...(categorySlug ? [{ name: tool.category, href: `/categories/${categorySlug}` }] : []),
            { name: tool.name, href: toolHref(tool) },
          ]}
        />

<div className="grid gap-6 lg:grid-cols-[1fr_300px]">
          {/* Main column */}
          <div className="min-w-0 max-w-none space-y-6">
            {/* Hero with Tool Interface */}
            <section id="hero" className="scroll-mt-24 space-y-3">
              {/* Category chip + tool header + favorite */}
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-medium text-[var(--color-accent-hover)]">
                  {tool.category}
                </span>
                {tool.trending && <Badge variant="default">Trending</Badge>}
                {tool.new && <Badge variant="new">New</Badge>}
                <FavoriteToggle slug={tool.slug} name={tool.name} />
              </div>
              <h1 className="text-2xl font-bold tracking-tight text-[var(--color-text)] sm:text-[1.75rem]">
                {tool.name}
              </h1>
              <p className="text-sm text-[var(--color-text-muted)] max-w-3xl text-pretty">
                {tool.description}
              </p>

              {/* Feature Badges - Key capabilities at a glance */}
              {content.features && content.features.length > 0 && (
                <div aria-label="Key features">
                  <FeatureBadgesGroup features={content.features} maxVisible={5} variant="brand" size="sm" />
                </div>
              )}

              <Suspense fallback={null}>
                <WorkflowTracker onUpdate={setWorkflowState} />
              </Suspense>

              {/* Workflow banner — shown when ?workflow is present */}
              {workflow && currentStepIdx >= 0 && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--radius-lg)] border border-[var(--color-accent)]/30 bg-[var(--color-accent-soft)] px-4 py-2.5 text-sm text-[var(--color-text)] shadow-[var(--shadow-xs)]">
                  <span className="font-medium text-[var(--color-text)]">
                    Workflow: {workflow.title} ({currentStepIdx + 1}/{workflow.steps.length})
                  </span>
                  <div className="flex items-center gap-2">
                    <Link
                      href={`/workflows/${workflow.slug}`}
                      className="text-xs font-medium text-[var(--color-accent-hover)] underline"
                    >
                      View workflow
                    </Link>
                    {nextWorkflowStep && (
                      <Button size="sm" variant="primary" onClick={handleWorkflowNext} className="h-7 gap-1">
                        Next: {nextWorkflowStep.label}
                        <ArrowRight className="h-3 w-3" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </div>
              )}

              {/* Tool Interface Card */}
              <div id={`tool-interface-${tool.slug}`} className="scroll-mt-24 overflow-hidden rounded-[var(--radius-xl)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-lg)]">
                <div className="h-1.5 w-full bg-gradient-to-r from-[var(--color-accent)] via-[var(--color-signal)] to-[var(--color-stamp)]" aria-hidden="true" />
                <div className="p-4 lg:p-6">
                  <ToolInterface slug={tool.slug} name={tool.name} />
                </div>
              </div>

              {/* Load-example affordance - lets a first-time user try the tool instantly */}
              {tool.examples && tool.examples.length > 0 && (
                <TryExamples
                  examples={tool.examples}
                  onExampleSelect={handleLoadExample}
                  label="Load example"
                  disabled={!toolReady}
                />
              )}
              {tool.examples && tool.examples.length > 0 && (
                <ExampleUrlListener slug={tool.slug} examples={tool.examples} ready={toolReady} />
              )}

              {/* Next Step CTA — India-first, Hybrid, URL-param per suggestions.md:29 */}
              {nextSteps && nextSteps.length > 0 && (
                <NextStepCTA suggestions={nextSteps} currentTool={tool.slug} />
              )}

              {/* Tool Actions - immediately accessible */}
              <ToolActions copied={copied} onCopy={() => handleCopy()} />

              {/* Embed widget - lets bloggers, SO answerers, and docs authors
                  paste a free iframe snippet; each embed is a backlink to
                  the canonical tool. Backlink strategy per PLAN.md. */}
              <div className="mt-3">
                <Suspense fallback={null}>
                  <EmbedWidget slug={tool.slug} title={tool.name} />
                </Suspense>
              </div>

              {/* Keyboard shortcut hint - tells power users the ? modal is reachable */}
              <p className="mt-2 flex items-center gap-1.5 text-xs text-[var(--color-text-muted)]">
                <Keyboard className="h-3.5 w-3.5" aria-hidden="true" />
                <span>
                  Press <kbd className="inline-flex h-4 min-w-4 items-center justify-center rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 font-mono text-[10px] text-[var(--color-text-muted)]" aria-hidden="true">?</kbd>{" "}
                  for keyboard shortcuts
                </span>
              </p>

              {/* Quick Links */}
              <QuickLinks tool={tool} specificGuide={specificGuide} categorySlug={categorySlug} mainSiteUrl={mainSiteUrl} toolsRepo={siteConfig.links.toolsRepo} />
              {/* Privacy promise — below workspace per DESIGN.md §93/§121#9 */}
              <div className="pt-2">
                <TrustPills />
              </div>
            </section>

            <InContentAd slot={adSlots.toolInContent1} />

            {/* Finance Disclaimer - only on financial tool pages */}
              {tool.category === "Finance" && (
                <Suspense fallback={null}>
                  <FinanceDisclaimer />
                </Suspense>
              )}

            {/* About */}
            {content.whatItDoes || content.whyItExists || content.whoShouldUse ? (
              <CollapsibleSection id="about" title="About" defaultOpen={false}>
                <div className="space-y-3 text-[var(--color-text-muted)]">
                  <p>{content.whatItDoes}</p>
                  <p>{content.whyItExists}</p>
                  <p className="text-sm text-[var(--color-text-muted)]">
                    This tool is part of the{" "}
                    <a href={mainSiteUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--color-accent-hover)] underline hover:text-[var(--color-accent-hover)]">
                      DevStackIO
                    </a>{" "}
                    platform — a collection of free online developer tools from DevStackIO.
                    Browse more free developer resources on{" "}
                    <a href={mainSiteUrl} target="_blank" rel="noopener noreferrer" className="text-[var(--color-accent-hover)] underline hover:text-[var(--color-accent-hover)]">
                      DevStackIO
                    </a>.
                  </p>
                  <div>
                    <h3 className="font-semibold text-[var(--color-text)]">Who should use this tool?</h3>
                    <p className="mt-1">{content.whoShouldUse}</p>
                  </div>
                  {content.useCases.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-[var(--color-text)]">Common use cases</h3>
                      <ul className="mt-2 space-y-1">
                        {content.useCases.map((uc, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                            <span>{uc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </CollapsibleSection>
            ) : null}

            {/* Key Features */}
            {content.features && content.features.length > 0 && (
              <CollapsibleSection id="features" title="Key Features" defaultOpen={false}>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {content.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5">
                      <CircleCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                      <span className="text-sm text-[var(--color-text-muted)]">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* How to use */}
            {content.instructions.length > 0 && (
              <CollapsibleSection id="how-to-use" title="How to use" defaultOpen>
                <div className="space-y-2.5">
                  {content.instructions.map((inst, i) => (
                    <div key={i} className="flex gap-3">
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent-hover)] text-sm font-semibold">
                        {i + 1}
                      </div>
                      <p className="text-[var(--color-text-muted)]">{inst}</p>
                    </div>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Examples */}
            {content.examples.length > 0 && (
              <CollapsibleSection id="examples" title="Examples">
                <div className="space-y-2.5">
                  {content.examples.map((ex, i) => (
                    <Card key={i} variant="outlined" padding="sm">
                      <pre tabIndex={0} className="overflow-x-auto whitespace-pre-wrap rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5 text-xs">
                        <code>{ex}</code>
                      </pre>
                    </Card>
                  ))}
                </div>
              </CollapsibleSection>
            )}

            {/* Best practices */}
            {content.bestPractices.length > 0 && (
              <CollapsibleSection id="best-practices" title="Best practices" icon={Lightbulb}>
                <ul className="space-y-2">
                  {content.bestPractices.map((bp, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Lightbulb className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-warning-800)]" aria-hidden="true" />
                      <span className="text-sm text-[var(--color-text-muted)]">{bp}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* Common mistakes */}
            {content.commonMistakes.length > 0 && (
              <CollapsibleSection id="common-mistakes" title="Common mistakes" icon={CircleAlert}>
                <ul className="space-y-2">
                  {content.commonMistakes.map((cm, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <CircleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-error-700)]" aria-hidden="true" />
                      <span className="text-sm text-[var(--color-text-muted)]">{cm}</span>
                    </li>
                  ))}
                </ul>
              </CollapsibleSection>
            )}

            {/* FAQ */}
            {content.faq.length > 0 && (
              <CollapsibleSection id="faq" title="FAQ" icon={CircleAlert}>
                <div className="space-y-2">
                  {content.faq.map((item, i) => {
                    const { question, answer } = parseFaqItem(item);
                    return (
                      <div key={i} className="rounded-md border border-[var(--color-border)] bg-[var(--color-bg)]">
                        <p className="px-4 pt-3 font-medium text-[var(--color-text)]">
                          {question}
                        </p>
                        <p className="px-4 pb-3 text-sm text-[var(--color-text-muted)]">
                          {answer}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </CollapsibleSection>
            )}

            {/* References */}
            {content.references && content.references.length > 0 && (
              <CollapsibleSection id="references" title="References" icon={BookOpen}>
                <p className="mb-3 text-sm text-[var(--color-text-muted)]">
                  Authoritative specifications, standards, and in-depth reading for {tool.name}.
                </p>
                <ul className="space-y-2">
                  {content.references.map((ref) => {
                    const isInternal = ref.url.startsWith("/");
                    if (isInternal) {
                      return (
                        <li key={`${ref.label}-${ref.url}`} className="flex items-start gap-2.5">
                          <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                          <Link href={ref.url} className="text-sm text-[var(--color-text-muted)] underline hover:text-[var(--color-accent-hover)]">
                            {ref.label}
                          </Link>
                        </li>
                      );
                    }
                    return (
                      <li key={`${ref.label}-${ref.url}`} className="flex items-start gap-2.5">
                        <ExternalLink className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                        <a
                          href={ref.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-[var(--color-accent-hover)] underline hover:text-[var(--color-accent-hover)]"
                        >
                          {ref.label}
                        </a>
                      </li>
                    );
                  })}
                </ul>
              </CollapsibleSection>
            )}

            <InContentAd slot={adSlots.toolInContent2} />

            {/* Share — direct app intents with original-color brand icons (SVGRepo) */}
            <CollapsibleSection id="share" title="Share" defaultOpen={false}>
              <p className="text-xs text-[var(--color-text-muted)]">
                Share directly to WhatsApp, X, Facebook, LinkedIn, Reddit, Pinterest, Telegram or Email — opens the app with the link preview pre-filled.
              </p>
              <Suspense fallback={null}>
                <ShareButtons
                  url={`${siteConfig.url}/tools/${tool.slug}`}
                  title={`${tool.name} — Free Online ${tool.category} Tool on DevStackIO`}
                  description={tool.description}
                />
              </Suspense>
              <a href={`#tool-interface-${tool.slug}`} className="inline-flex min-h-[44px] items-center text-sm font-medium text-[var(--color-accent-hover)] underline underline-offset-4">
                ↑ Back to {tool.name} tool
              </a>
            </CollapsibleSection>
          </div>

          {/* Sidebar */}
          <aside className="space-y-6 lg:sticky lg:top-24 lg:self-start">
            <SidebarAd slot={adSlots.toolSidebar} />
            {/* Live network proof stays in the sidebar; the static privacy
                promise moved below the H1 (TrustPills). */}
            <div
              className="flex flex-col gap-2"
              role="list"
              aria-label="Tool guarantees"
            >
              <span
                className="inline-flex items-center gap-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-3 py-1.5 text-xs font-medium text-[var(--color-text)]"
                role="listitem"
                title={networkPillTitle}
                aria-label={requestCount === 0 ? "Browser-only processing. No tool data was sent." : "Browser-only processing. Tool data stays local."}
              >
                <Activity
                  className="h-3.5 w-3.5 text-[var(--color-accent-hover)]"
                  aria-hidden="true"
                />
                <span data-testid="network-request-count">
                  {requestCount === 0
                    ? "Browser-only processing"
                    : "Tool data stays local"}
                </span>
              </span>
            </div>

            {/* Related Tools */}
            <section id="related-tools" className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 lg:p-6 space-y-3">
              <SectionHeading>Related Tools</SectionHeading>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <Suspense fallback={null}>
                  {relatedList.map((rt) => (
                    <ToolCard
                      key={rt.id}
                      tool={{
                        id: rt.id,
                        name: rt.name,
                        description: rt.description,
                        category: rt.category,
                        slug: rt.slug,
                        popularity: rt.popularity,
                        featured: rt.featured,
                        trending: rt.trending,
                        new: rt.new,
                        icon: rt.icon,
                      }}
                      variant="compact"
                      size="sm"
                      showPopularity={false}
                    />
                  ))}
                </Suspense>
              </div>
            </section>

            <Suspense fallback={null}>
              <GlossaryRelated toolSlug={tool.slug} />
            </Suspense>

            {/* Learning Resources */}
            <section id="learning-resources" className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-4 lg:p-6 space-y-3">
              <SectionHeading icon={BookOpen}>Learning Resources</SectionHeading>
              <div className="space-y-2">
                {specificGuide && (
                  <Link
                    href={`/guides/${specificGuide.slug}`}
                    className="group flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5 transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
                  >
                    <div className="flex items-start gap-2">
                      <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-medium text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
                          {specificGuide.title}
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">{specificGuide.readTime} read</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
                  </Link>
                )}
                {categorySlug && (
                  <Link
                    href={`/categories/${categorySlug}`}
                    className="group flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5 transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
                  >
                    <div className="flex items-start gap-2">
                      <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                      <div>
                        <p className="text-sm font-medium text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
                          More {tool.category} Tools
                        </p>
                        <p className="text-xs text-[var(--color-text-muted)]">Browse the full category</p>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 flex-shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
                  </Link>
                )}
                <Link
                  href="/guides"
                  className="group flex items-center justify-between gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] p-2.5 transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-soft)]"
                >
                  <div className="flex items-start gap-2">
                    <BookOpen className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent-hover)]" aria-hidden="true" />
                    <div>
                      <p className="text-sm font-medium text-[var(--color-text)] group-hover:text-[var(--color-accent)]">
                        Developer Guides
                      </p>
                      <p className="text-xs text-[var(--color-text-muted)]">In-depth tutorials and best practices</p>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 flex-shrink-0 text-[var(--color-text-muted)]" aria-hidden="true" />
                </Link>
              </div>
            </section>
          </aside>
        </div>
      </div>
    </>
  );
}